import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Save, Settings, ArrowLeft, Plus, Trash2, AlertCircle, Calculator, Award, DollarSign, TrendingUp } from 'lucide-react';
import BackButton from '../components/BackButton';
import { getToken } from '../services/tokenService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminSimulatorConfig = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [salaryLevels, setSalaryLevels] = useState([]);
  const [creditPacks, setCreditPacks] = useState([]);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    loadConfiguration();
  }, []);

  const loadConfiguration = async () => {
    setLoading(true);
    try {
      const token = getToken();
      
      // Charger les niveaux de salariés
      const salaryResponse = await axios.get(
        `${BACKEND_URL}/api/admin/simulator/salary-levels`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSalaryLevels(salaryResponse.data.salary_levels || []);
      
      // Charger les packs de crédits
      const packsResponse = await axios.get(
        `${BACKEND_URL}/api/admin/simulator/credit-packs`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCreditPacks(packsResponse.data.credit_packs || []);
      
    } catch {
      setErrorMessage('Erreur lors du chargement de la configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSalaryLevelChange = (index, field, value) => {
    const updated = [...salaryLevels];
    updated[index] = { ...updated[index], [field]: value };
    setSalaryLevels(updated);
  };

  const handleCreditPackChange = (index, field, value) => {
    const updated = [...creditPacks];
    const pack = { ...updated[index], [field]: value };
    
    // Recalculer total_credits si price ou bonus change
    if (field === 'price' || field === 'bonus') {
      const baseCredits = parseInt(pack.price) || 0;
      const bonus = parseInt(pack.bonus) || 0;
      pack.total_credits = baseCredits + bonus;
    }
    
    updated[index] = pack;
    setCreditPacks(updated);
  };

  const addCreditPack = () => {
    const newPack = {
      id: `pack_${Date.now()}`,
      name: `Nouveau Pack`,
      price: 0,
      bonus: 0,
      total_credits: 0,
      order: creditPacks.length + 1
    };
    setCreditPacks([...creditPacks, newPack]);
  };

  const removeCreditPack = (index) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce pack ?')) {
      const updated = creditPacks.filter((_, i) => i !== index);
      setCreditPacks(updated);
    }
  };

  const handleSaveSalaryLevels = async () => {
    setSaving(true);
    setSuccessMessage('');
    setErrorMessage('');
    
    try {
      const token = getToken();
      await axios.put(
        `${BACKEND_URL}/api/admin/simulator/salary-levels`,
        { salary_levels: salaryLevels },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSuccessMessage('Niveaux de salariés sauvegardés avec succès !');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch {
      setErrorMessage('Erreur lors de la sauvegarde des niveaux');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCreditPacks = async () => {
    setSaving(true);
    setSuccessMessage('');
    setErrorMessage('');
    
    try {
      const token = getToken();
      await axios.put(
        `${BACKEND_URL}/api/admin/simulator/credit-packs`,
        { credit_packs: creditPacks },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSuccessMessage('Packs de crédits sauvegardés avec succès !');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch {
      setErrorMessage('Erreur lors de la sauvegarde des packs');
    } finally {
      setSaving(false);
    }
  };

  const handleInitialize = async () => {
    if (!window.confirm('Réinitialiser avec les valeurs par défaut ? Cela écrasera la configuration actuelle.')) {
      return;
    }
    
    try {
      const token = getToken();
      await axios.post(
        `${BACKEND_URL}/api/admin/simulator/initialize`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSuccessMessage('Configuration réinitialisée !');
      setTimeout(() => setSuccessMessage(''), 3000);
      loadConfiguration();
    } catch {
      setErrorMessage('Erreur lors de la réinitialisation');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        
      {/* Bouton Retour */}
      <BackButton to="/admin/dashboard" label="Retour" />

<div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button
            onClick={() => navigate('/admin/gestion')}
            variant="ghost"
            className="mb-2"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour au tableau de bord
          </Button>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <Calculator className="h-8 w-8 text-blue-600" />
                Configuration du Simulateur O'SCOP GE
              </h1>
              <p className="text-gray-600 mt-2">
                Paramétrez les tarifs et les packs de crédits du simulateur
              </p>
            </div>
            
            <Button
              onClick={handleInitialize}
              variant="outline"
              className="border-orange-600 text-orange-600"
            >
              <Settings className="h-4 w-4 mr-2" />
              Réinitialiser
            </Button>
          </div>
        </div>

        {/* Messages */}
        {successMessage && (
          <div className="mb-6 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
            ✅ {successMessage}
          </div>
        )}
        
        {errorMessage && (
          <div className="mb-6 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            {errorMessage}
          </div>
        )}

        {/* Section 1: Niveaux de salariés */}
        <Card className="mb-6 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white">
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-6 w-6" />
              1. Niveaux de Salariés
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-4">
              {salaryLevels.map((level, index) => (
                <div key={index} className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Nom du niveau
                    </label>
                    <input
                      type="text"
                      value={level.label}
                      onChange={(e) => handleSalaryLevelChange(index, 'label', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Crédits / heure
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={level.credits_per_hour}
                      onChange={(e) => handleSalaryLevelChange(index, 'credits_per_hour', parseInt(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Icône (emoji)
                    </label>
                    <input
                      type="text"
                      value={level.icon}
                      onChange={(e) => handleSalaryLevelChange(index, 'icon', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-center text-2xl"
                      maxLength="2"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Couleur (Tailwind)
                    </label>
                    <select
                      value={level.color}
                      onChange={(e) => handleSalaryLevelChange(index, 'color', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="bg-blue-500">Bleu</option>
                      <option value="bg-green-500">Vert</option>
                      <option value="bg-purple-500">Violet</option>
                      <option value="bg-red-500">Rouge</option>
                      <option value="bg-yellow-500">Jaune</option>
                      <option value="bg-pink-500">Rose</option>
                    </select>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 flex justify-end">
              <Button
                onClick={handleSaveSalaryLevels}
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Sauvegarde...' : 'Sauvegarder les niveaux'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Section 2: Packs de crédits */}
        <Card className="shadow-lg">
          <CardHeader className="bg-gradient-to-r from-green-500 to-emerald-500 text-white">
            <CardTitle className="flex items-center gap-2">
              <Award className="h-6 w-6" />
              2. Packs de Crédits
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-4">
              {creditPacks.map((pack, index) => (
                <div key={index} className="grid grid-cols-1 md:grid-cols-6 gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Nom du pack
                    </label>
                    <input
                      type="text"
                      value={pack.name}
                      onChange={(e) => handleCreditPackChange(index, 'name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Prix (€)
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={pack.price}
                      onChange={(e) => handleCreditPackChange(index, 'price', parseInt(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Bonus
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={pack.bonus}
                      onChange={(e) => handleCreditPackChange(index, 'bonus', parseInt(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Total crédits
                    </label>
                    <div className="w-full px-3 py-2 bg-gray-200 border border-gray-300 rounded-lg text-center font-bold text-green-600">
                      {pack.total_credits}
                    </div>
                  </div>

                  <div className="flex items-end">
                    <Button
                      onClick={() => removeCreditPack(index)}
                      variant="outline"
                      className="w-full border-red-500 text-red-500 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 flex justify-between items-center">
              <Button
                onClick={addCreditPack}
                variant="outline"
                className="border-green-600 text-green-600"
              >
                <Plus className="h-4 w-4 mr-2" />
                Ajouter un pack
              </Button>

              <Button
                onClick={handleSaveCreditPacks}
                disabled={saving}
                className="bg-green-600 hover:bg-green-700"
              >
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Sauvegarde...' : 'Sauvegarder les packs'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Preview */}
        <Card className="mt-6 shadow-lg border-2 border-blue-300">
          <CardHeader className="bg-blue-50">
            <CardTitle className="flex items-center gap-2 text-blue-900">
              <DollarSign className="h-6 w-6" />
              Aperçu des tarifs
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-bold text-gray-900 mb-3">Niveaux de salariés</h3>
                <div className="space-y-2">
                  {salaryLevels.map((level, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-white rounded border">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">{level.icon}</span>
                        <span className="font-semibold">{level.label}</span>
                      </div>
                      <span className="text-blue-600 font-bold">{level.credits_per_hour} cr/h</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="font-bold text-gray-900 mb-3">Packs de crédits</h3>
                <div className="space-y-2">
                  {creditPacks.map((pack, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-white rounded border">
                      <div>
                        <div className="font-semibold">{pack.name}</div>
                        {pack.bonus > 0 && (
                          <div className="text-xs text-green-600">+{pack.bonus} bonus</div>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="text-blue-600 font-bold">{pack.price}€</div>
                        <div className="text-xs text-gray-600">{pack.total_credits} crédits</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminSimulatorConfig;
